import React, { useState } from 'react';
import { Icon, IconFlip } from '@progress/kendo-react-common';
import { Reveal } from '@progress/kendo-react-animation';
import { Button } from '@progress/kendo-react-buttons';
import { ChatSource } from '../../../../types';

interface ChatSourcesSectionProps {
    sources: ChatSource[];
}

interface GroupedSources {
    [key: string]: ChatSource[];
}

export const ChatSourcesSection: React.FC<ChatSourcesSectionProps> = ({ sources }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [iconFlip, setIconFlip] = useState<IconFlip>('default');
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

    const MAX_VISIBLE_SOURCES = 5;

    const toggleExpanded = () => {
        setIsExpanded(prev => !prev);
        setIconFlip(prev => prev === 'default' ? 'vertical' : 'default');
    };

    const toggleGroupExpanded = (groupType: string) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupType]: !prev[groupType]
        }));
    };

    const groupSourcesByType = (): GroupedSources => {
        const seen = new Set<string>();
        
        return sources.reduce((groups: GroupedSources, source) => {
            const key = `${source.title.toLowerCase()}|${source.text.toLowerCase()}`;
            
            if (seen.has(key)) {
                return groups;
            }
            
            seen.add(key);
            
            if (!groups[source.type]) {
                groups[source.type] = [];
            }
            groups[source.type].push(source);
            return groups;
        }, {});
    };

    const getGroupTitle = (type: string): string => {
        switch (type) {
            case 'Internet Search':
                return 'Internet Search';
            case 'Dataset':
                return 'Dataset Results';
            default:
                return 'Other Sources';
        }
    };

    const getSourceIcon = (type: string): string => {
        switch (type) {
            case 'Internet Search':
                return 'rss';
            case 'Dataset':
                return 'file-txt';
            default:
                return 'file';
        }
    };

    const renderSourcesList = () => {
        if (!isExpanded) return null;

        const groupedSources = groupSourcesByType();

        return (
            <div className='chat-sources-list'>
                {Object.entries(groupedSources).map(([type, group], groupIndex) => {
                    const isGroupExpanded = expandedGroups[type] ?? false;
                    const visibleSources = isGroupExpanded 
                        ? group 
                        : group.slice(0, MAX_VISIBLE_SOURCES);
                    const hasMore = group.length > MAX_VISIBLE_SOURCES;

                    return (
                        <div className='chat-source-group' key={`source-group-${groupIndex}`}>
                            <span className='chat-source-group-title'>
                                {getGroupTitle(type)} ({group.length})
                            </span>
                            {visibleSources.map((source, sourceIndex) => (
                                <div className='chat-source-item' key={`source-item-${sourceIndex}`}>
                                    <Icon name={getSourceIcon(source.type)} />
                                    <div className='chat-source-item-content'>
                                        {source.link && source.link !== '#' ? (
                                            <a 
                                                href={source.link} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className='chat-source-item-content-title chat-source-link'
                                            >
                                                {source.title}
                                            </a>
                                        ) : (
                                            <span className='chat-source-item-content-title'>
                                                {source.title}
                                            </span>
                                        )}
                                        <p className='chat-source-item-content-text'>
                                            {source.text}
                                        </p>
                                    </div>
                                </div>
                            ))}
                            {hasMore && (
                                <Button 
                                    className='chat-source-show-more'
                                    fillMode={'clear'}
                                    themeColor={'primary'}
                                    onClick={() => toggleGroupExpanded(type)}
                                >
                                    {isGroupExpanded 
                                        ? 'Show Less' 
                                        : `Show More (${group.length - MAX_VISIBLE_SOURCES} more)`
                                    }
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className='chat-sources'>
            <div className='chat-sources-header'>
                <div className='chat-sources-title'>
                    <Icon name="file-txt" />
                    <span className='chat-sources-title-text'>
                        Sources: {sources.length}
                    </span>
                </div>
                <Icon 
                    flip={iconFlip} 
                    name="chevron-down" 
                    onClick={toggleExpanded} 
                />
            </div>
            <Reveal componentChildClassName="chat-sources-container">
                {renderSourcesList()}
            </Reveal>
        </div>
    );
};
